import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Gift, Settings } from "lucide-react";

export default function Premium() {
  const { user, refresh } = useAuth() || {};
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => { api.get("/premium/plans").then(r => setPlans(r.data.plans || [])); }, []);

  const subscribe = async (plan) => {
    if (!user) { toast.error("Sign in first"); nav("/login"); return; }
    setLoading(plan);
    try {
      const { data } = await api.post("/premium/subscribe", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setLoading(null); }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/me/billing-portal", { return_url: window.location.origin + "/premium" });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Portal unavailable"); }
  };

  const perks = [
    "Ad-free viewing across all pages",
    "Early access to new sermons + book series episodes",
    "Exclusive Premium-only worship concerts",
    "Download PDFs from the VOT Living Book Series",
    "Priority customer support",
  ];

  return (
    <div className="pt-16" data-testid="premium-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-14">
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-3 flex items-center justify-center gap-2"><Zap className="w-3 h-3"/>VOTLiving Premium</p>
          <h1 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">Watch Uninterrupted. Grow Deeper.</h1>
          <p className="text-white/70 max-w-2xl mx-auto">A membership tier for viewers who want a distraction-free experience — no ads, exclusive content, and downloadable resources.</p>
          {user?.premium && <div className="mt-6 inline-flex items-center gap-3">
            <div className="px-4 py-2 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-semibold" data-testid="premium-active-badge">You're Premium ✨</div>
            <button onClick={openPortal} className="btn-ghost rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="manage-subscription-btn"><Settings className="w-4 h-4"/>Manage subscription</button>
          </div>}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          {plans.map(p => (
            <div key={p.key} className={`relative rounded-2xl border p-8 ${p.key === "annual" ? "border-[#D4AF37]/50 bg-gradient-to-br from-[#0F0F14] to-[#0A0A0E] shadow-[0_10px_60px_rgba(212,175,55,0.12)]" : "border-white/10 bg-[#0A0A0E]"}`} data-testid={`plan-${p.key}`}>
              {p.savings && <div className="absolute -top-3 right-6 bg-[#D4AF37] text-[#050507] text-[10px] font-bold tracking-widest px-3 py-1 rounded-full">{p.savings}</div>}
              {p.key === "annual" && <div className="absolute -top-3 left-6 bg-white text-[#050507] text-[10px] font-bold tracking-widest px-3 py-1 rounded-full flex items-center gap-1"><Zap className="w-3 h-3"/>7-DAY FREE TRIAL</div>}
              <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-1">{p.interval === "year" ? "Annual" : "Monthly"}</p>
              <h3 className="font-heading text-3xl font-bold mb-2">{p.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-heading text-5xl font-bold text-[#D4AF37]">${p.price_usd.toFixed(p.key === "annual" ? 0 : 2)}</span>
                <span className="text-sm text-white/50">/{p.interval}</span>
              </div>
              <button onClick={()=>subscribe(p.key)} disabled={loading===p.key || user?.premium} className="w-full btn-gold py-3 rounded-full font-semibold disabled:opacity-60" data-testid={`subscribe-${p.key}`}>
                {user?.premium ? "You're Premium" : loading === p.key ? "Redirecting…" : p.key === "annual" ? "Start free trial" : "Subscribe"}
              </button>
              <p className="text-[11px] text-white/40 mt-3 text-center">{p.key === "annual" ? "First 7 days free — cancel any time" : "Cancel any time from your account"}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          <Link to="/gift-premium" className="bg-gradient-to-br from-[#0F0F14] to-[#0A0A0E] border border-[#D4AF37]/30 rounded-2xl p-6 flex items-start gap-4 group hover:border-[#D4AF37]/60 transition-colors" data-testid="gift-cta">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0"><Gift className="w-6 h-6 text-[#D4AF37]"/></div>
            <div>
              <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-1">Bless Someone</p>
              <h4 className="font-heading text-xl font-bold mb-2">Gift a Premium membership</h4>
              <p className="text-sm text-white/60">Send 1, 3, 6, or 12 months to a family, friend, or ministry partner. Save up to 26%.</p>
            </div>
          </Link>
          <Link to="/bulk-gift" className="bg-gradient-to-br from-[#0F0F14] to-[#0A0A0E] border border-[#D4AF37]/30 rounded-2xl p-6 flex items-start gap-4 group hover:border-[#D4AF37]/60 transition-colors" data-testid="bulk-gift-cta">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-[#D4AF37]"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><circle cx="17" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg></div>
            <div>
              <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-1">For Pastors + Ministries</p>
              <h4 className="font-heading text-xl font-bold mb-2">Bulk gift (10 / 25 / 50 seats)</h4>
              <p className="text-sm text-white/60">Buy 12-month Premium memberships in bulk at up to 30% off. Perfect for congregations.</p>
            </div>
          </Link>
          <Link to="/redeem-gift" className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6 flex items-start gap-4 group hover:border-white/25 transition-colors md:col-span-2" data-testid="redeem-cta">
            <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0"><Sparkles className="w-6 h-6 text-white/70"/></div>
            <div>
              <p className="text-[10px] tracking-widest uppercase text-white/50 mb-1">Received a Gift?</p>
              <h4 className="font-heading text-xl font-bold mb-2">Redeem your code</h4>
              <p className="text-sm text-white/60">Enter your gift code to activate Premium instantly.</p>
            </div>
          </Link>
        </div>

        <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6"><Sparkles className="w-4 h-4 text-[#D4AF37]"/><h3 className="font-heading text-2xl font-bold">What's included</h3></div>
          <ul className="grid md:grid-cols-2 gap-3">
            {perks.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-white/80"><div className="w-6 h-6 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-[#D4AF37]"/></div>{p}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
